import { apiFetch } from './client.js';

export interface TenantUsageSummary {
  tenantId: string;
  slug: string;
  name: string;
  plan: string | null;
  status: string;
  subscriptionStartedAt: string | null;
  subscriptionExpiresAt: string | null;
  periodFrom: string;
  periodTo: string;
  periodCostUsd: number;
  annualCostUsd: number;
  periodTurns: number;
  periodInputTokens: number;
  periodOutputTokens: number;
}

export interface MonthlyBucket {
  month: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  turns: number;
}

export interface BreakdownRow {
  key: string;
  costUsd: number;
  turns: number;
  inputTokens: number;
  outputTokens: number;
}

export interface UserUsageSummary {
  userId: string;
  email: string;
  name: string | null;
  role: string | null;
  plan: string | null;
  periodCostUsd: number;
  annualCostUsd: number;
  periodTurns: number;
}

export interface TenantDetailResult {
  tenant: TenantUsageSummary;
  monthly: MonthlyBucket[];
  byModel: BreakdownRow[];
  byFeature: BreakdownRow[];
  users: UserUsageSummary[];
}

export interface UserDetailResult {
  user: UserUsageSummary & { tenantId: string };
  monthly: MonthlyBucket[];
  byModel: BreakdownRow[];
  byFeature: BreakdownRow[];
}

export interface RawUsageEvent {
  id: string;
  occurredAt: string;
  userId: string | null;
  userEmail: string | null;
  feature: string;
  provider: string;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number;
}

export interface RawUsageEventsPage {
  total: number;
  items: RawUsageEvent[];
  limit: number;
  offset: number;
}

export interface UsageRangeQuery {
  from?: string;
  to?: string;
  plan?: 'start' | 'pro' | 'teams' | 'vitruvio';
  search?: string;
}

function qs(params: { [k: string]: string | number | undefined }): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export async function listStaffTenantsUsage(
  q: UsageRangeQuery = {},
): Promise<{ tenants: TenantUsageSummary[] }> {
  return apiFetch(`/api/v1/staff/usage/tenants${qs({ ...q })}`);
}

export async function getStaffTenantUsage(
  tenantId: string,
  q: { from?: string; to?: string } = {},
): Promise<TenantDetailResult> {
  return apiFetch(`/api/v1/staff/usage/tenants/${tenantId}${qs(q)}`);
}

export async function getStaffUserUsage(
  tenantId: string,
  userId: string,
  q: { from?: string; to?: string } = {},
): Promise<UserDetailResult> {
  return apiFetch(`/api/v1/staff/usage/tenants/${tenantId}/users/${userId}${qs(q)}`);
}

export async function listStaffTenantEvents(
  tenantId: string,
  q: { userId?: string; from?: string; to?: string; limit?: number; offset?: number } = {},
): Promise<RawUsageEventsPage> {
  return apiFetch(`/api/v1/staff/usage/tenants/${tenantId}/events${qs(q)}`);
}

// ── Corridas: tokens/USD por fase por cotización + reúso de biblioteca ────────

export interface RunPhaseRow {
  phase: string;
  provider: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface RunUsageSummary {
  quoteId: string;
  tenantId: string;
  tenantSlug: string;
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  lastAt: string;
  phases: RunPhaseRow[];
}

export interface ApuLibraryStats {
  libraryRows: number;
  libraryApproved: number;
  libraryReuses: number;
  periodApus: number;
  periodFromLibrary: number;
  hitRate: number | null;
}

export async function listStaffRunsUsage(
  q: { from?: string; to?: string; limit?: number } = {},
): Promise<{ runs: RunUsageSummary[] }> {
  return apiFetch(`/api/v1/staff/usage/runs${qs({ ...q })}`);
}

export async function getStaffApuLibraryStats(
  q: { from?: string; to?: string } = {},
): Promise<ApuLibraryStats> {
  return apiFetch(`/api/v1/staff/usage/apu-library${qs({ ...q })}`);
}
